const pdfjsLib = require('pdfjs-dist/build/pdf.js')

export const fileAdapter = {
  isImage: (file: File):boolean => file.type.substring(0,5) == "image",
  isPdf: (file: File):boolean => file.type == "application/pdf",
  asDataUrl: async (file: File) : Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.readAsDataURL(file)
      reader.onload = () => resolve(typeof reader.result == 'string' ? reader.result : "")
      reader.onerror = error => reject(`Error reading file as base64 URL: ${error}`)
    })
  },
  rename: (file: File, newName: string) : File => {
    const renamedFile = new File([file], newName, { type: file.type })
    return renamedFile
  },
  getExtension: (file: File) : string => file.name.split('.').pop()?.toLowerCase() || "",
}

export const dataUrlAdapter = {
  asFile: async (dataUrl: string, fileName: string) : Promise<File> => {
    const res: Response = await fetch(dataUrl)
    const blob: Blob = await res.blob()
    return new File([blob], fileName, { type: dataUrlAdapter.getMimeType(dataUrl) })
  },
  getMimeType: (dataUrl: string) => {
    return dataUrl.substring(dataUrl.indexOf(":")+1, dataUrl.indexOf(";"))
  },
}


export const imgAdapter = {
  getHeightAndWidth: async (file: File) : Promise<{height: number, width: number}> => {
    const fileUrl = await fileAdapter.asDataUrl(file)

    return new Promise(resolve => {
      const tempImg = new Image()
      tempImg.onload = () => {
        resolve({
          height: tempImg.height,
          width: tempImg.width
        })
      }
      tempImg.src = fileUrl
    })
  },
  limitSize: async (file: File, max: {height: number, width: number}) : Promise<File> => {
    const fileAsUrl = await fileAdapter.asDataUrl(file)
    const dimensions = await imgAdapter.getHeightAndWidth(file)
    const mime = dataUrlAdapter.getMimeType(fileAsUrl)
    const fileName = file.name

    // Set height and width based on max height and width
    let width = dimensions.width
    let height = dimensions.height

    if (width > height) {
      if (width > max.width) {
        height *= max.width / width
        width = max.width
      }
    } else {
      if (height > max.height) {
        width *= max.height / height
        height = max.height
      }
    }

    // Resize using canvas
    const $img = document.createElement('img')
    $img.src = fileAsUrl

    const $canvas = document.createElement('canvas')
    const canvasContext = $canvas.getContext('2d')

    canvasContext!.imageSmoothingEnabled = false // Make sure not to blur the image
    
    $canvas.width = width
    $canvas.height = height

    canvasContext?.drawImage($img, 0, 0, width, height)

    const imgAsDataUrl = $canvas.toDataURL(mime || "image/png", mime == "image/jpeg" ? 0.98 : 1)

    return await dataUrlAdapter.asFile(imgAsDataUrl, fileName)
  }
}

export const pdfAdapter = {
  getPagesAsImageDataUrls: async (file: File) => {
    const fileAsBase64 = await fileAdapter.asDataUrl(file)

    // Set PDF.js worker: (located in /public directory)
    pdfjsLib.GlobalWorkerOptions.workerSrc = "/lib/pdf.worker.min.js"

    // Load PDF document 
    const loadingDocument = pdfjsLib.getDocument(fileAsBase64)
    const loadedPDF = await loadingDocument.promise

    const pageNumbersAsArr = Array.from(Array(loadedPDF.numPages).keys()).map(n => n + 1)

    const pagesAsDataUrls = await Promise.all(pageNumbersAsArr.map(async pageNum => {
      const page = await loadedPDF.getPage(pageNum)
      
      // Render PDF in canvas and export as image data URL
      const pdfViewport = page.getViewport({ scale: 1.85 }) // Higher number is a better quality
      const $canvas = document.createElement('canvas')
      const canvasContext = $canvas.getContext('2d')
      $canvas.height = pdfViewport.height
      $canvas.width = pdfViewport.width

      await page.render({
        canvasContext,
        viewport: pdfViewport
      }).promise

      return $canvas.toDataURL('image/jpeg', 0.9) // jpeg quality - 1 = best
    }))

    return pagesAsDataUrls
  },
}